/**
 * Pure rules for the audit-log export form, mirroring what the API enforces so
 * a person is told before the request — the API still has the last word.
 */
import type { AuditExportFilters } from '../../services/auditLogExport.service';

/** Mirrors the API: the span between `from` and `to` may not exceed this many days. */
export const MAX_RANGE_DAYS = 92;
export const MAX_FILTER_LENGTH = 100;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DAY = /^\d{4}-\d{2}-\d{2}$/;
const MS_PER_DAY = 86_400_000;

export interface ExportForm {
  from: string;
  to: string;
  actorId: string;
  action: string;
  targetType: string;
  targetId: string;
}

export const EMPTY_FORM: ExportForm = {
  from: '', to: '', actorId: '', action: '', targetType: '', targetId: '',
};

function dayNumber(value: string): number | null {
  if (!DAY.test(value)) return null;
  const t = Date.parse(`${value}T00:00:00Z`);
  return Number.isNaN(t) ? null : Math.round(t / MS_PER_DAY);
}

/** The reason the export cannot run yet, worded for the screen — null when it can. */
export function exportBlockedReason(form: ExportForm): string | null {
  if (!form.from || !form.to) return 'Choose the first and last day to export.';
  const start = dayNumber(form.from);
  const end = dayNumber(form.to);
  if (start === null || end === null) return 'Enter both days as real dates.';
  if (end < start) return 'The last day cannot be before the first day.';
  if (end - start > MAX_RANGE_DAYS) {
    return `Choose at most ${MAX_RANGE_DAYS} days between the first and last day.`;
  }
  if (form.actorId.trim() && !UUID.test(form.actorId.trim())) {
    return 'The actor ID must be a UUID, for example 0b6f3c1e-8a52-4c1e-9d7a-2f4e5a6b7c8d.';
  }
  if (form.targetId.trim() && !UUID.test(form.targetId.trim())) {
    return 'The target ID must be a UUID, for example 0b6f3c1e-8a52-4c1e-9d7a-2f4e5a6b7c8d.';
  }
  if (
    form.action.trim().length > MAX_FILTER_LENGTH ||
    form.targetType.trim().length > MAX_FILTER_LENGTH
  ) {
    return `Action and target type are at most ${MAX_FILTER_LENGTH} characters.`;
  }
  return null;
}

/** Form fields -> request filters; blank filters are dropped. */
export function toFilters(form: ExportForm): AuditExportFilters {
  const trimmed = (v: string): string | undefined => v.trim() || undefined;
  return {
    from: form.from,
    to: form.to,
    actorId: trimmed(form.actorId),
    action: trimmed(form.action),
    targetType: trimmed(form.targetType),
    targetId: trimmed(form.targetId),
  };
}
